import { Check, ShieldCheck } from 'lucide-react';
import type { LearningFieldMeasurement } from '../shared/desktop-api';
import { LearningTrialSummary } from './LearningTrialSummary';
import { learningFieldTargetKey } from './learning-attempts';

export function LearningFieldProgress({
  measurements,
  failed,
  onRetry,
  onInspect,
}: {
  measurements: LearningFieldMeasurement[];
  failed: boolean;
  onRetry: () => void;
  onInspect?: (targetKey: string) => void;
}) {
  if (!measurements.length && !failed) return null;
  // Each pair has its own untouched population. Never draw a cross-session
  // trend line that silently compares different sets of documents.
  return (
    <section className="learning-session-report" aria-label="Measured field improvements">
      <header>
        <ShieldCheck size={20} />
        <div>
          <span className="eyebrow">Checked against your confirmations</span>
          <h2>Field learning results</h2>
        </div>
      </header>
      {failed && (
        <div role="status">
          <strong>
            {measurements.length
              ? 'Could not refresh · showing last received field results'
              : 'Field learning results could not be read'}
          </strong>{' '}
          <button type="button" className="quiet-button small" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}
      <div className="learning-field-results">
        {measurements.slice(0, 6).map((row) => (
          <article key={`${row.sessionId}:${row.evidenceKey}:${row.recordedAt}`}>
            <header>
              <div>
                <strong>{row.field}</strong>
                <small>{row.documentType}</small>
              </div>
              <span
                className={
                  row.applied || row.attempts?.some((attempt) => attempt.adopted)
                    ? 'learning-field-adopted'
                    : ''
                }
              >
                {row.applied || row.attempts?.some((attempt) => attempt.adopted) ? (
                  <>
                    <Check size={15} /> Saved after this check
                  </>
                ) : row.outcome === 'validated' ? (
                  'Checked · not saved'
                ) : (
                  'No change'
                )}
              </span>
            </header>
            {row.judged > 0 ? (
              <>
                <div className="learning-field-comparison">
                  <span>Before</span>
                  <progress
                    aria-label={`${row.field} before`}
                    value={row.beforeRight}
                    max={row.judged}
                  />
                  <b>
                    {row.beforeRight}/{row.judged}
                  </b>
                </div>
                <div className="learning-field-comparison">
                  <span>New approach</span>
                  <progress
                    aria-label={`${row.field} candidate`}
                    value={row.afterRight}
                    max={row.judged}
                  />
                  <b>
                    {row.afterRight}/{row.judged}
                  </b>
                </div>
                <small>
                  {row.judged}{' '}
                  {row.evidenceKey.startsWith('format:') ||
                  row.evidenceKey.startsWith('instruction:')
                    ? 'field values on untouched documents'
                    : 'untouched documents'}{' '}
                  ·{' '}
                  {row.regressions === 0
                    ? 'No previously correct answers lost'
                    : `${row.regressions} previously correct answers lost — change not saved`}
                </small>
              </>
            ) : (
              <>
                <LearningTrialSummary evidenceKey={row.evidenceKey} attempts={row.attempts} />
                <small>
                  No final check:{' '}
                  {row.outcome === 'no-candidates'
                    ? 'no new guidance to try'
                    : row.outcome === 'operator-guidance'
                      ? 'your saved reading instructions were preserved'
                      : row.outcome === 'operator-refused'
                        ? 'you turned down a change to this reading earlier, so none was tested'
                        : row.outcome === 'validation-already-reserved'
                          ? 'confirm more documents to provide a fresh independent check; your existing reading is unchanged'
                          : row.outcome === 'insufficient-independent-originals'
                            ? 'more distinct confirmed documents are needed; repeated imports do not count as new examples'
                            : row.outcome === 'evidence-changed'
                              ? 'your setup or confirmations changed'
                              : 'no approach qualified for further testing'}
                  .
                </small>
              </>
            )}
            {onInspect && !!row.attempts?.length && (
              <button
                type="button"
                className="quiet-button small"
                onClick={() => onInspect(learningFieldTargetKey(row))}
              >
                View approaches
              </button>
            )}
            <details>
              <summary>Measurement details</summary>
              <small>
                {new Date(row.recordedAt).toLocaleString()}
                <br />
                {row.modelReads == null
                  ? 'Reading count not recorded'
                  : `${row.modelReads} reading passes`}{' '}
                · field-reading check, not whole-job accuracy
                <br />
                This records the outcome of this check. A saved change may no longer be in use.
                <br />
                Current use and processing speed are measured separately.
              </small>
            </details>
          </article>
        ))}
      </div>
    </section>
  );
}
